/**
 * Hook useEntityOutlooks - Synchronizes outlooks with entity data
 * For overseas territories that have specific RWL values
 */
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setEntityOutlooks, resetOutlook } from '../../store/slices/outlookSlice';
import { parseHorizon } from '../../utils/outlookHelpers';

/**
 * Hook to synchronize outlooks with entity data
 * Overseas territories can have custom RWL values in their `horizon` property
 *
 * @param {Object} entity - Entity data (commune/EPCI)
 * @param {string|Array<string>} [entity.horizon] - Entity-specific RWL values (serialized or array)
 *
 * @example
 * const { data: entity } = useEntity(insee);
 * useEntityOutlooks(entity);
 */
const useEntityOutlooks = (entity) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!entity) {
      // No entity loaded, do nothing
      return;
    }

    // Parse and convert entity-specific horizons
    const parsedHorizons = parseHorizon(entity.horizon);

    if (parsedHorizons) {
      // Entity has specific horizons (typically overseas)
      dispatch(setEntityOutlooks({ horizons: parsedHorizons }));
    } else {
      // Entity uses default horizons
      dispatch(setEntityOutlooks({ horizons: null }));
    }

    // Cleanup: reset to default values on unmount
    return () => {
      dispatch(resetOutlook());
    };
  }, [entity, dispatch]);
};

export default useEntityOutlooks;
